import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  FlexBox,
  Spacer
} from '@directsoftware/ui-kit-web-admin';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWlsRoomAvailability, fetchWlsRoomPricing, fetchWlsCheckoutAvailability } from '../../utils/wls-api-client';
import {
  availabilityFromError,
  createRequestTracker,
  invalidDatesAvailability,
  isDisplayableDateRangeError
} from '../../utils/wls-booking-range';
import { useDetectMobile } from '../../../shared/hooks/useDetectMobile';
import Meta from './meta';
import PhotoGrid from './shared-v2/photo-grid';
import ListingName from './shared-v2/listing-name';
import ListingSubHeadline from './shared-v2/listing-subheadline';
import FooterFinal from '../layout/footer-final';
import FeatureList from './shared-v2/feature-list';
import DetailsTabNav from './shared-v2/details-tab-nav';
import CheckInCheckOut from './shared-v2/check-in-check-out';
import Description from './shared-v2/description';
import ChooseRoom from './room-v2/choose-room';
import RentalRules from './shared-v2/rental-rules';
import ManagerInfo from './shared-v2/manager-info';
import LocationMap from './shared-v2/location-map';
import Reviews from './shared-v2/reviews';
import BookingSidebar from './shared-v2/booking-sidebar';
import BedroomsList from './shared-v2/bedrooms-list';
import BathroomsList from './shared-v2/bathrooms-list';
import TermsAndConditions from './shared-v2/terms-and-conditions';
import CancellationPolicy from './shared-v2/cancellation-policy';
import CheckInCheckOutPolicy from './shared-v2/check-in-check-out-policy';
import {
  selectListingsDetailsSingleState,
  updateListingsDetailsSingleSlice
} from '../../redux/slices/listingsDetailsSingle';
import { selectUiState, updateUiSlice } from '../../redux/slices/ui';
import {
  parseQuery,
  updateQueryString
} from '../listings/resources/shared-functions';

const RoomV2 = props => {
  const { isLargeDesktop, isMobile } = useDetectMobile();
  const dispatch = useDispatch();
  const brand = useSelector(state => state.brand);
  const listing = useSelector(state => state.listing);
  const listingsDetailsState = useSelector(selectListingsDetailsSingleState);
  const { listingsDetailsChanged } = useSelector(selectUiState);

  const updateDetailsSingleState = (data, isFilterChange = false) => {
    dispatch(updateListingsDetailsSingleSlice(data));
    if (isFilterChange)
      dispatch(updateUiSlice({ listingsDetailsChanged: true }));
  };

  // A response is only applied when it comes from the newest request of its
  // kind and the range it was issued for is still in state, so a slow reply
  // for an earlier range or guest count cannot overwrite a newer one.
  const currentBookingRange = useRef(listingsDetailsState.bookingRange);
  currentBookingRange.current = listingsDetailsState.bookingRange;
  const availabilityRequests = useRef(createRequestTracker()).current;
  const pricingRequests = useRef(createRequestTracker()).current;
  const trackRequest = (requests, range) => {
    const token = requests.start();
    return () =>
      requests.isLatest(token) && currentBookingRange.current === range;
  };

  const handleBrowserState = () => {
    const queryInfo = parseQuery(location.search, brand);

    updateDetailsSingleState(
      {
        bookingRange: queryInfo.bookingRange || null,
        bookingLength: queryInfo.bookingRange
          ? queryInfo.bookingRange.length - 1
          : 0,
        checkInDate: queryInfo.checkIn || null,
        checkOutDate: queryInfo.checkOut || null,
        room: listing.room_type ? queryInfo.room : undefined,
        ...(isDisplayableDateRangeError(queryInfo.bookingRangeError) && {
          availability: invalidDatesAvailability(queryInfo.bookingRangeError),
          pricing: null
        }),
        guests: queryInfo.guests || 1,
        isDirty: true,
        datesParsed: true
      },
      true
    );
  };

  const checkAvailability = () => {
    const requestedRange = listingsDetailsState.bookingRange;
    const isLatestRequest = trackRequest(availabilityRequests, requestedRange);

    const wlsRequest = listing.room_type
      ? fetchWlsRoomAvailability(brand, listing.id, {
          unit_id: listing.unit.id,
          booking_range: JSON.stringify(listingsDetailsState.bookingRange),
          guests: listingsDetailsState.guests
        })
      : fetchWlsCheckoutAvailability(brand, listing.id, {
          unit_id: listing.unit.id,
          booking_range: JSON.stringify(listingsDetailsState.bookingRange),
          guests: listingsDetailsState.guests
        });
    wlsRequest
      .then(response => {
        if (!isLatestRequest()) return;
        updateDetailsSingleState({ availability: response.data });
      })
      .catch(error => {
        if (!isLatestRequest()) return;
        const availability = availabilityFromError(error);
        if (availability) updateDetailsSingleState({ availability });
        console.log(error);
      });
  };

  const checkPricing = () => {
    const requestedRange = listingsDetailsState.bookingRange;
    const isLatestRequest = trackRequest(pricingRequests, requestedRange);

    fetchWlsRoomPricing(brand, listing.id, {
      booking_range: JSON.stringify(listingsDetailsState.bookingRange),
      num_guests: listingsDetailsState.guests,
      addon_fee_ids: listingsDetailsState.addonFeeIds,
      coupon_code: listingsDetailsState.couponCode
    })
      .then(response => {
        if (!isLatestRequest()) return;
        updateDetailsSingleState({ pricing: response.data });
      })
      .catch(error => { console.log(error); });
  };

  useEffect(
    () => {
      if (listingsDetailsChanged) {
        dispatch(updateUiSlice({ listingsDetailsChanged: false }));
        if (listingsDetailsState.bookingRange) {
          checkAvailability();
          checkPricing();
          updateQueryString(listingsDetailsState);
        }
      }
    },
    [listingsDetailsState, listingsDetailsChanged]
  );

  useEffect(() => {
    handleBrowserState();
    window.onpopstate = handleBrowserState;
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) element.scrollIntoView();
    }
  }, []);

  return (
    <>
      <Meta />
      {!isMobile && <Spacer size="m" />}
      <Container variation={isMobile ? 'full-width' : 'fluid-wls'}>
        <PhotoGrid />
      </Container>
      <Spacer size="m" />
      <Container variation="extended-wls">
        <FlexBox className="detailsLayout">
          <Box flex="1">
            <ListingName />
            <Spacer size="xxs" />
            <ListingSubHeadline />
            <Spacer />
            <FeatureList />
            {listing.room_types?.length >= 1 && <ChooseRoom />}
            {!isMobile && <Spacer size="m" />}
            <DetailsTabNav />
            <Spacer size="l" />
            <Box id="details-overview" />
            <CheckInCheckOut />
            <Spacer size="l" />
            <Description />
            {(listing.unit.num_bedrooms >= 0 ||
              listing.unit.num_sleep_in_beds >= 0) && <BedroomsList />}
            {listing.unit.num_bathrooms && <BathroomsList />}
            <TermsAndConditions />
            {listing.refund_policy && <CancellationPolicy />}
            {listing.availability.check_in_check_out_policy && (
              <CheckInCheckOutPolicy />
            )}
            {listing.property.summary_accommodations && <RentalRules />}
            {listing.property_manager && <ManagerInfo />}
          </Box>
          {isLargeDesktop ? (
            <Box className="detailsLayout__right">
              <Box className="detailsLayout__bookingSidebar">
                <BookingSidebar
                  updateDetailsSingleState={updateDetailsSingleState}
                />
              </Box>
            </Box>
          ) : (
            <>
              <Box className="detailsLayout__mobileFooter">
                <BookingSidebar
                  updateDetailsSingleState={updateDetailsSingleState}
                />
              </Box>
            </>
          )}
        </FlexBox>
        <Box id="details-location" />
        <LocationMap />
        {listing.reviews.length > 0 && (
          <>
            <Box id="details-reviews" />
            <Reviews />
          </>
        )}
        <Spacer size="l" />
      </Container>
      <FooterFinal />
    </>
  );
};

export default RoomV2;
